import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { BarChart3, Plus, X, ArrowRight, RefreshCw, Lock } from "lucide-react";
import { formatCurrency } from "../../lib/helpers";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const ALL_MARKETS = [
  { slug: "tampa-fl", label: "Tampa, FL" },
  { slug: "orlando-fl", label: "Orlando, FL" },
  { slug: "miami-fl", label: "Miami, FL" },
  { slug: "jacksonville-fl", label: "Jacksonville, FL" },
  { slug: "dallas-tx", label: "Dallas, TX" },
  { slug: "houston-tx", label: "Houston, TX" },
  { slug: "san-antonio-tx", label: "San Antonio, TX" },
  { slug: "austin-tx", label: "Austin, TX" },
  { slug: "atlanta-ga", label: "Atlanta, GA" },
  { slug: "charlotte-nc", label: "Charlotte, NC" },
  { slug: "nashville-tn", label: "Nashville, TN" },
  { slug: "phoenix-az", label: "Phoenix, AZ" },
  { slug: "las-vegas-nv", label: "Las Vegas, NV" },
  { slug: "denver-co", label: "Denver, CO" },
  { slug: "indianapolis-in", label: "Indianapolis, IN" },
  { slug: "memphis-tn", label: "Memphis, TN" },
  { slug: "cleveland-oh", label: "Cleveland, OH" },
  { slug: "detroit-mi", label: "Detroit, MI" },
  { slug: "kansas-city-mo", label: "Kansas City, MO" },
  { slug: "columbus-oh", label: "Columbus, OH" },
];

const METRICS = [
  { key: "total_listings", label: "Active Listings", fmt: (v) => v.toLocaleString(), higher: "better" },
  { key: "avg_price", label: "Avg Price", fmt: (v) => formatCurrency(v), higher: "neutral" },
  { key: "median_price", label: "Median Price", fmt: (v) => formatCurrency(v), higher: "neutral" },
  { key: "avg_rent", label: "Avg Rent/mo", fmt: (v) => formatCurrency(v), higher: "better" },
  { key: "price_per_sqft", label: "Price/sqft", fmt: (v) => `$${v.toLocaleString()}`, higher: "worse" },
  { key: "est_cap_rate", label: "Est. Cap Rate", fmt: (v) => `${v}%`, higher: "better" },
  { key: "price_to_rent_ratio", label: "Price-to-Rent", fmt: (v) => `${v}x`, higher: "worse" },
  { key: "est_monthly_cash_flow", label: "Est. Cash Flow/mo", fmt: (v) => formatCurrency(v), higher: "better" },
  { key: "seized_count", label: "Seized Properties", fmt: (v) => v.toLocaleString(), higher: "neutral" },
];

const SUGGESTIONS = [
  { cities: ["tampa-fl", "jacksonville-fl"], label: "Tampa vs Jacksonville" },
  { cities: ["dallas-tx", "houston-tx"], label: "Dallas vs Houston" },
  { cities: ["atlanta-ga", "charlotte-nc"], label: "Atlanta vs Charlotte" },
  { cities: ["phoenix-az", "las-vegas-nv"], label: "Phoenix vs Las Vegas" },
  { cities: ["indianapolis-in", "cleveland-oh"], label: "Indianapolis vs Cleveland" },
  { cities: ["detroit-mi", "memphis-tn"], label: "Detroit vs Memphis" },
];

const FREE_METRICS = ["total_listings", "avg_price", "median_price", "avg_rent"];

const MarketCompareTab = ({ subscription, onUpgrade }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (selected.length >= 2) {
      setLoading(true);
      axios
        .get(`${API}/market-compare`, { params: { cities: selected.join(",") } })
        .then((r) => setData(r.data.markets || []))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    } else {
      setData(null);
    }
  }, [selected]);

  const addMarket = (slug) => {
    if (selected.length < 4 && !selected.includes(slug)) {
      setSelected([...selected, slug]);
    }
    setDropdownOpen(false);
  };

  const removeMarket = (slug) => setSelected(selected.filter((s) => s !== slug));
  const getLabel = (slug) => ALL_MARKETS.find((m) => m.slug === slug)?.label || slug;
  const available = ALL_MARKETS.filter((m) => !selected.includes(m.slug));

  const getBestIdx = (key, metric) => {
    if (!data || data.length < 2) return -1;
    const vals = data.map((m) => m[key] || 0);
    if (metric.higher === "better") return vals.indexOf(Math.max(...vals));
    if (metric.higher === "worse") return vals.indexOf(Math.min(...vals));
    return -1;
  };

  return (
    <div data-testid="market-compare-tab">
      {/* City Picker */}
      <div className="mb-6" data-testid="compare-picker">
        <p className="text-sm text-muted-foreground mb-3">
          Pick 2–4 cities to compare side-by-side on price, rent, cap rate, cash flow, and more.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {selected.map((slug) => (
            <div
              key={slug}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-sm font-medium text-emerald-500"
            >
              {getLabel(slug)}
              <button
                onClick={() => removeMarket(slug)}
                className="hover:text-red-400 transition-colors"
                data-testid={`compare-remove-${slug}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {selected.length < 4 && (
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/50 border border-border text-sm text-muted-foreground hover:text-foreground hover:border-emerald-500/30 transition-all"
                data-testid="compare-add-city"
              >
                <Plus className="w-3.5 h-3.5" /> Add City
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-1 left-0 z-50 bg-card border border-border rounded-lg shadow-xl max-h-60 overflow-y-auto w-56">
                  {available.map((m) => (
                    <button
                      key={m.slug}
                      onClick={() => addMarket(m.slug)}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-secondary/50 transition-colors"
                      data-testid={`compare-option-${m.slug}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {selected.length < 2 && (
          <p className="text-xs text-muted-foreground mt-2">Select at least 2 cities to compare.</p>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Comparison Table */}
      {data && data.length >= 2 && !loading && (
        <div className="overflow-x-auto" data-testid="compare-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-xs text-muted-foreground uppercase font-semibold w-48">
                  Metric
                </th>
                {data.map((m) => (
                  <th key={m.slug} className="text-center py-3 px-4 min-w-[180px]">
                    <span className="text-base font-bold text-foreground">{m.city}</span>
                    <span className="text-xs text-muted-foreground ml-1">{m.state}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRICS.map((metric) => {
                const isPro = subscription?.is_paid;
                const isLocked = !isPro && !FREE_METRICS.includes(metric.key);
                const bestIdx = getBestIdx(metric.key, metric);
                return (
                  <tr key={metric.key} className={`border-b border-border/50 hover:bg-secondary/20 transition-colors ${isLocked ? 'relative' : ''}`}>
                    <td className="py-3 px-4 text-sm text-muted-foreground font-medium">
                      {metric.label}
                      {isLocked && <Lock className="w-3 h-3 inline-block ml-1.5 text-amber-500" />}
                    </td>
                    {data.map((m, idx) => {
                      const val = m[metric.key] || 0;
                      const isBest = bestIdx === idx && metric.higher !== "neutral" && data.length > 1;
                      return (
                        <td key={m.slug} className="text-center py-3 px-4" data-testid={`compare-cell-${metric.key}-${m.slug}`}>
                          {isLocked ? (
                            <span className="font-mono text-sm font-semibold text-muted-foreground/30 blur-sm select-none">
                              {metric.fmt(val)}
                            </span>
                          ) : (
                            <span className={`font-mono text-sm font-semibold ${isBest ? "text-emerald-500" : "text-foreground"}`}>
                              {metric.fmt(val)}
                            </span>
                          )}
                          {isBest && (
                            <Badge className="ml-1.5 text-[8px] bg-emerald-500/10 text-emerald-500 border-emerald-500/20 px-1 py-0">
                              Best
                            </Badge>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="border-b border-border/50">
                <td className="py-3 px-4 text-sm text-muted-foreground font-medium">Property Types</td>
                {data.map((m) => (
                  <td key={m.slug} className="py-3 px-4">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {Object.entries(m.property_type_breakdown || {})
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 4)
                        .map(([type, count]) => (
                          <Badge key={type} variant="secondary" className="text-[9px] capitalize">
                            {type.replace("_", " ")} ({count})
                          </Badge>
                        ))}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* CTA under table */}
      {data && data.length >= 2 && !loading && (
        <div className="space-y-4 mt-8">
          {!subscription?.is_paid && (
            <div className="text-center p-4 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid="compare-upgrade-cta">
              <p className="text-sm text-muted-foreground mb-2">
                Cap rate, cash flow, and price-to-rent data are available with Pro.
              </p>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => onUpgrade && onUpgrade("analysis")}>
                Unlock Full Comparison
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-3 justify-center">
          {data.map((m) => (
            <Button
              key={m.slug}
              variant="outline"
              size="sm"
              onClick={() => navigate(`/investment-properties/${m.slug}`)}
              data-testid={`compare-view-${m.slug}`}
            >
              View {m.city} Properties <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          ))}
          </div>
        </div>
      )}

      {/* Suggestion cards */}
      {(!data || data.length < 2) && !loading && (
        <div>
          <h3 className="text-base font-bold text-center mb-6">Popular Comparisons</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[900px] mx-auto">
            {SUGGESTIONS.map(({ cities, label }) => (
              <Card
                key={label}
                className="bg-card border-border cursor-pointer card-hover"
                onClick={() => setSelected(cities)}
                data-testid={`compare-suggestion-${cities.join("-")}`}
              >
                <CardContent className="p-4 text-center">
                  <BarChart3 className="w-6 h-6 text-violet-500 mx-auto mb-2" />
                  <p className="font-semibold text-sm">{label}</p>
                  <p className="text-xs text-muted-foreground mt-1">Click to compare</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketCompareTab;
